import { activeWear, type Account } from '@ofc/shared';
import { Avatar, PlayerTitle, nameStyle, plateStyle, BannerLayer, bannerClass } from '@ofc/ui';

/**
 * You, as everybody else sees you: your plate, frame, font and title, exactly
 * as your card looks in a lobby, at the top of the home page.
 *
 * Whatever you put on for an event shows here while the event is on, the same as
 * it does in a room, and quietly goes back to your usual look when it ends.
 */
export function NameCard({
  user,
  open,
  onToggle,
  streak,
}: {
  user: Account;
  open: boolean;
  onToggle(): void;
  /** The daily streak, when there is one going. */
  streak: { days: number; daysLeft: number | null } | null;
}) {
  const event = activeWear(user.seasonal);
  const font = event?.font ?? user.avatarFont;
  const look = {
    emoji: user.avatarEmoji,
    color: user.avatarColor,
    userId: user.id,
    picture: user.avatarPicture,
    frame: event?.frame ?? user.avatarFrame,
  };
  const banner = event?.banner ?? user.avatarBanner;

  return (
    <section
      className={`panel marquee row entrance-${event?.entrance ?? user.avatarEntrance ?? 'fade'}${bannerClass(banner)}`}
      style={{
        padding: '14px 12px',
        gap: 12,
        minHeight: 78,
        borderColor: user.avatarColor,
        boxShadow: `0 0 18px color-mix(in srgb, ${user.avatarColor} 30%, transparent)`,
        ...plateStyle(user.avatarPlate, user.avatarColor),
      }}
    >
      <BannerLayer banner={banner} colour={user.avatarColor} />
      <Avatar player={look} size={44} />
      <span className="grow col" style={{ gap: 2, minWidth: 0 }}>
        <span className="truncate" style={{ color: user.avatarColor, fontSize: 20 }}>
          <span style={nameStyle(font, 20)}>{user.username}</span>
        </span>
        <PlayerTitle title={user.avatarTitle} colour={user.avatarColor} />
        {streak && streak.days > 0 && (
          <span className="small" style={{ color: streak.daysLeft === null ? 'var(--text)' : 'var(--amber)' }}>
            🔥 {streak.days}-day streak
            {streak.daysLeft === 0 && ' · play a daily today to keep it'}
            {streak.daysLeft !== null && streak.daysLeft > 0 && ` · ${streak.daysLeft + 1} days to keep it`}
          </span>
        )}
      </span>
      <button className="btn-ghost small" onClick={onToggle} style={{ flexShrink: 0 }}>
        {open ? 'Close' : 'Profile'}
      </button>
    </section>
  );
}
